import { KANBAN_EVENTS } from "../../common/kanban.constant";
import { WINAPP_METHOD } from "../../common/win-app-method.constants";
import type { ITaskPrefill } from "../../models/task";

export type KanbanWebviewAction =
    | { kind: "winapp"; method: string; args: Record<string, number> }
    | { kind: "person-detail"; personId: number }
    | { kind: "task-drawer"; prefill: ITaskPrefill }
    | { kind: "drop" };

// Shape of a Kanban iframe postMessage; every field is unvalidated.
interface KanbanMessage {
    TYPE?: unknown;
    type?: unknown;
    ID?: unknown;
    data?: unknown;
}

interface KanbanTaskPayload {
    propertyId?: unknown;
    personId?: unknown;
    title?: unknown;
    personReference?: unknown;
}

const DROP: KanbanWebviewAction = { kind: "drop" };

// Kanban sends ids as numbers or numeric strings; anything else (booleans, arrays,
// fractions) is no id.
function toId(value: unknown): number | null {
    if (typeof value !== "number" && typeof value !== "string") return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function winApp(method: string, argName: string, id: number | null): KanbanWebviewAction {
    return id === null ? DROP : { kind: "winapp", method, args: { [argName]: id } };
}

/**
 * What the WinApp webview does with a Kanban iframe message instead of the desktop
 * router navigation. null = not a redirect event: the shared desktop handling runs
 * (URL_CHANGE, new-relation, unknown types). "drop" = a redirect event whose payload
 * is unusable: nothing may run, above all not the desktop router.push.
 * loadedProperty = the lead the host has loaded; its Reference labels the task's
 * property only when it is the same property (it can lag behind a lead switch).
 */
export function resolveKanbanWebviewAction(
    data: unknown,
    loadedProperty?: { Id?: number; Reference?: string } | null
): KanbanWebviewAction | null {
    const message: KanbanMessage = data !== null && typeof data === "object" ? data : {};
    switch (message.TYPE || message.type) {
        case KANBAN_EVENTS.EDIT_PERSON:
            return winApp(WINAPP_METHOD.PERSON.OPEN_PERSON_EDIT, "PersonId", toId(message.ID));
        case KANBAN_EVENTS.PROPERTY_EDIT:
            return winApp(WINAPP_METHOD.PROPERTY.OPEN_PROPERTY_EDIT, "PropertyId", toId(message.ID));
        case KANBAN_EVENTS.CREATE_APPOINTMENT:
            return winApp(WINAPP_METHOD.AGENDA.NEW_APPOINTMENT, "PropertyId", toId(message.ID));
        case KANBAN_EVENTS.VIEW_PERSON: {
            const personId = toId(message.ID);
            return personId === null ? DROP : { kind: "person-detail", personId };
        }
        case KANBAN_EVENTS.CREATE_TASK: {
            const p: KanbanTaskPayload = message.data !== null && typeof message.data === "object" ? message.data : {};
            const propertyId = toId(p.propertyId);
            const personId = toId(p.personId);
            if (propertyId === null || personId === null) return DROP;

            // TASK_PREFILL_FIELDS has no Date, so the payload date is not carried over.
            const prefill: ITaskPrefill = { ObjectId: propertyId, PersonId: personId };
            const personRef = p.personReference === null || p.personReference === undefined ? "" : String(p.personReference);
            if (loadedProperty?.Id === propertyId && loadedProperty.Reference) prefill.ObjectRef = loadedProperty.Reference;
            if (personRef) prefill.PersonRef = personRef;
            if (typeof p.title === "string" && p.title) prefill.Subject = p.title;
            return { kind: "task-drawer", prefill };
        }
        default:
            return null;
    }
}
